"use client"

import { useState, useRef, useCallback, useEffect, type ReactNode } from "react"
import { cn } from "@/lib/utils"
import { motion } from "@/components/insurance/static-motion"
import "./coverage-form.css"
import { useInsuranceTranslation } from "@/lib/insurance/use-insurance-translation"
import {
  findInsurer,
  findService,
  getServiceName,
  effectiveServicePrice,
  formatCHF,
  shouldShowProforma,
} from "@/lib/insurance/insurers"
import {
  getInsurerLogoUrl,
  getServiceIcon,
  assetUrl,
} from "@/lib/insurance/insurer-assets"
import {
  trackEvent,
  captureException,
  isFetchNetworkError,
  getPostHogDistinctId,
  isInternalEmail,
} from "@/lib/insurance/insurance-tracking"
import { InsurerDropdown } from "@/components/insurance/insurer-dropdown"
import { ServiceDropdown } from "@/components/insurance/service-dropdown"
import { AddonSelector } from "@/components/insurance/addon-selector"
import {
  prefetchAddons,
  getAddonsForService,
  type Addon,
} from "@/lib/insurance/addons"
import {
  COVERAGE_PRESELECT_EVENT,
  type CoveragePreselectDetail,
} from "@/components/insurance/coverage-preselect"
import { isMembershipService } from "@/lib/insurance/booking-deeplink"
import type { InsurerData, Locale } from "@/lib/insurance/insurance-types"
import { EMAIL_REGEX } from "@/lib/insurance/email-regex"
import { CoverageResult } from "@/components/insurance/coverage-result"
import { computePanelResult } from "@/lib/insurance/panel-result"
import { Receipt } from "@phosphor-icons/react"

// How long the "calculating your coverage" loader shows after the last
// selection, before the email gate reveals. The work is visibly done BEFORE
// the email is asked for — the email then only unlocks a result that already
// exists (proof-of-labour ordering), instead of the loader running after the
// gate as a spinner the user has to sit through having already paid with
// their address.
const CALCULATING_MS = 1800

/** The tier the check-up step opens on: the Ahead check-up once a year. */
const DEFAULT_MEMBERSHIP_ID = "membership-basic"

/** Fire-and-forget POST — never blocks the UI. */
function fireAndForget(path: string, payload: Record<string, unknown>): void {
  const url = path
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).catch((err) => {
    // Per-user network failures (offline, ad blocker, navigated away mid-request)
    // are unactionable — see CLAUDE.md i18n notes.
    if (isFetchNetworkError(err)) return
    captureException(
      new Error(`fireAndForget ${path}: ${err?.message ?? err}`),
      {
        source: "fireAndForget",
        path,
        url,
        originalError: String(err),
      }
    )
  })
}

// select → calculating (loader) → gate-email (result is "ready", email
// unlocks it) → result. The three non-select phases each replace the
// accordion card with their own card.
type Phase = "select" | "calculating" | "gate-email" | "result"
type Step = "insurer" | "plan" | "service" | "addons"

/** Card treatment. `tinted` = white card with a soft shadow and neutral-50
 *  option surfaces inside, for pages that put the checker across a white
 *  hero band and the grey page (/insurance). */
export type CoverageTone = "default" | "tinted"

interface CoverageFormProps {
  data: InsurerData
  embedded?: boolean
  tone?: CoverageTone
  /**
   * Insurer pre-selected on mount (the per-insurer pages, `/insurance/<id>`).
   * The insurer step renders completed and the accordion opens on the plan
   * step; the visitor can still click back and change it.
   */
  initialInsurerId?: string
}

/* ── Accordion step wrapper ── */
function AccordionStep({
  stepNumber,
  label,
  isActive,
  isCompleted,
  isEnabled = true,
  onActivate,
  summary,
  aside,
  children,
}: {
  stepNumber: number
  label: string
  isActive: boolean
  isEnabled?: boolean
  isCompleted: boolean
  onActivate: () => void
  summary?: ReactNode
  /** Right-hand slot on the header row while the step is active (e.g. the
   *  add-ons running total). */
  aside?: ReactNode
  children: ReactNode
}) {
  return (
    <div
      className={cn(
        "accordion-step",
        isActive && "active",
        isCompleted && "completed",
        !isActive && !isCompleted && !isEnabled && "disabled"
      )}
    >
      {/* Header — clickable when completed */}
      <button
        type="button"
        className="accordion-step-header"
        onClick={isCompleted ? onActivate : undefined}
        disabled={!isCompleted}
        aria-expanded={isActive}
        aria-label={`${label}, step ${stepNumber}${isCompleted ? ", completed — click to edit" : ""}`}
      >
        <span
          className={cn(
            "accordion-step-number",
            isCompleted && !isActive && "done",
            isActive && "current"
          )}
        >
          {isCompleted && !isActive ? (
            <img
              src={assetUrl("/check.svg")}
              alt=""
              width={24}
              height={24}
              className="accordion-step-check"
            />
          ) : (
            stepNumber
          )}
        </span>
        <span className="accordion-step-text">
          <span className="accordion-step-label">{label}</span>
          {isCompleted && !isActive && summary && (
            <span className="accordion-step-summary">{summary}</span>
          )}
        </span>
        {isActive && aside && (
          <span className="accordion-step-aside">{aside}</span>
        )}
        {isCompleted && !isActive && (
          <svg
            className="accordion-step-edit"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            width="16"
            height="16"
          >
            <path d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
          </svg>
        )}
      </button>

      {/* Expandable content — GPU-only opacity + transform */}
      {isActive && (
        <div className="accordion-step-content accordion-step-enter">
          {children}
        </div>
      )}
    </div>
  )
}

export function CoverageForm({
  data,
  embedded = false,
  tone = "default",
  initialInsurerId,
}: CoverageFormProps) {
  const { t, locale } = useInsuranceTranslation()
  // Card shell shared by every phase (accordion, loader, gate, result).
  const cardClass = cn(
    "mx-auto max-w-[760px] scroll-mt-28 rounded-2xl px-4 py-6 sm:p-7 sm:px-8",
    tone === "tinted"
      ? "coverage-tinted bg-white shadow-[0_1px_8px_rgba(0,0,0,0.04)]"
      : "bg-white"
  )
  const hasStarted = useRef(false)

  // Selection state
  const [insurerId, setInsurerId] = useState(initialInsurerId ?? "")
  const [packageId, setPackageId] = useState("")
  // The check-up being priced. Holds a membership tier id when a membership is
  // the only thing selected — that is a quotable service in its own right (its
  // coverage falls back to the package's blood-test rule).
  const [serviceId, setServiceId] = useState(DEFAULT_MEMBERSHIP_ID)
  // The membership tier selected ALONGSIDE the check-up, kept separately so
  // the two coexist: picking Core MRI under a chosen tier must not discard the
  // tier, it must price the MRI at the member rate. Never empty: every
  // check-up is booked as the Ahead check-up (the advanced blood test) at
  // 1x, 2x or 4x a year, an MRI only on top of it, so 1x is picked up front.
  const [membershipId, setMembershipId] = useState(DEFAULT_MEMBERSHIP_ID)
  const [insurerFreetext, setInsurerFreetext] = useState("")
  const [packageFreetext, setPackageFreetext] = useState("")
  const [, setPackageHint] = useState(false)

  // Gate state
  const [phase, setPhase] = useState<Phase>("select")
  const [email, setEmail] = useState("")
  const [submitError, setSubmitError] = useState(false)
  // Inline "enter a valid email" hint — the submit button is always active
  // (a greyed-out button reads as disabled), so validation happens on click.
  const [emailInvalid, setEmailInvalid] = useState(false)

  // After the last selection step: run the "calculating" loader, then reveal
  // the email gate with the result marked as ready.
  const revealAfterSelections = useCallback(() => {
    if (submitTimer.current) clearTimeout(submitTimer.current)
    setPhase("calculating")
    submitTimer.current = setTimeout(() => {
      setPhase("gate-email")
      setTimeout(() => emailRef.current?.focus(), 250)
    }, CALCULATING_MS)
  }, [])

  // Pro forma request state
  const [showProformaForm, setShowProformaForm] = useState(false)
  const [proformaFirstName, setProformaFirstName] = useState("")
  const [proformaLastName, setProformaLastName] = useState("")
  const [proformaGender, setProformaGender] = useState("")
  const [proformaStatus, setProformaStatus] = useState<
    "idle" | "submitting" | "success" | "error" | "async_fallback"
  >("idle")
  const [proformaPdfUrl, setProformaPdfUrl] = useState<string | null>(null)
  const [proformaError, setProformaError] = useState<string | null>(null)

  // Accordion state
  const [activeStep, setActiveStep] = useState<Step>("insurer")
  const manualNav = useRef(false) // true when user clicked back to edit

  // Add-on state
  const [selectedAddons, setSelectedAddons] = useState<Map<string, Addon>>(
    new Map()
  )
  const [availableAddons, setAvailableAddons] = useState<Addon[]>([])
  const [, setAddonsLoading] = useState(false)

  const emailRef = useRef<HTMLInputElement>(null)
  // Result/loader card — scrolled into view when it appears (the user has
  // usually scrolled past the form's start by the time they submit).
  const resultRef = useRef<HTMLDivElement>(null)
  const submitTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Prefetch add-ons on mount
  useEffect(() => {
    prefetchAddons()
  }, [])

  const trackStart = useCallback(() => {
    if (!hasStarted.current) {
      hasStarted.current = true
      trackEvent("insurance_form_started", { locale })
    }
  }, [locale])

  // Determine if selections are complete enough
  const insurer = insurerId ? findInsurer(data, insurerId) : undefined
  const isOther = insurerId === "other"
  const needsPackage = insurer && insurer.packages.length > 0
  // Show plan step by default — only hide for "Other" (no packages)
  const showPlanStep = !isOther
  const hasPackage = !!packageId
  const insurerReady = !!(insurerId && (!needsPackage || hasPackage || isOther))

  // Auto-advance: insurer selected → plan step (if has packages) or service step
  // Skip for "other" — user needs to type their insurer name first
  useEffect(() => {
    if (
      insurerId &&
      insurerId !== "other" &&
      activeStep === "insurer" &&
      !manualNav.current
    ) {
      const ins = findInsurer(data, insurerId)
      const nextStep = ins && ins.packages.length > 0 ? "plan" : "service"
      const timer = setTimeout(() => setActiveStep(nextStep), 300)
      return () => clearTimeout(timer)
    }
  }, [insurerId, activeStep])

  // Auto-advance: plan selected → service step
  useEffect(() => {
    if (hasPackage && activeStep === "plan" && !manualNav.current) {
      const timer = setTimeout(() => setActiveStep("service"), 300)
      return () => clearTimeout(timer)
    }
  }, [hasPackage, activeStep])

  // Note: service → addons/gate auto-advance is handled in handleServiceChange

  // The preselected tier is the quoted service until a check-up is picked, so
  // its add-on offer loads once the insurer is known: the step's Continue
  // then leads to the add-ons rather than straight to the estimate.
  useEffect(() => {
    if (insurerReady && isMembershipService(serviceId)) {
      loadAddons(serviceId, true)
    }
  }, [insurerReady]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleInsurerChange(id: string) {
    trackStart()
    manualNav.current = false

    // If clicking the same insurer, just advance to next step
    if (id === insurerId) {
      manualNav.current = true
      const ins = findInsurer(data, id)
      if (ins && ins.packages.length > 0) {
        setActiveStep("plan")
      } else {
        setActiveStep("service")
      }
      return
    }

    setInsurerId(id)
    setPackageId("")
    setPackageHint(false)
    setInsurerFreetext("")
    setPackageFreetext("")
    setServiceId(DEFAULT_MEMBERSHIP_ID)
    setMembershipId(DEFAULT_MEMBERSHIP_ID)
    setPhase("select")

    const ins = id === "other" ? null : findInsurer(data, id)
    const insName = id === "other" ? t("form.insurer.other") : ins?.name || id
    trackEvent("insurance_form_insurer_selected", {
      insurer_id: id,
      insurer_name: insName,
    })
  }

  function handlePackageChange(id: string) {
    manualNav.current = true // prevent auto-advance chain — let user confirm service
    setPackageId(id)
    if (id !== packageId) setPackageFreetext("") // only reset when package actually changes
    setPackageHint(false)

    // Stay on plan step when "Other" is selected so user can type the package name
    if (!id.endsWith("-other")) {
      setTimeout(() => setActiveStep("service"), 300)
    }

    const ins = findInsurer(data, insurerId)
    const pkg = ins?.packages.find((p) => p.id === id)
    trackEvent("insurance_form_package_selected", {
      package_id: id,
      package_name: pkg?.name || id,
    })
  }

  // Per-insurer pages: a package card below the form selects that package
  // here (see coverage-preselect.ts). Re-bound on every selection change so
  // the handler always compares against the current insurer.
  useEffect(() => {
    function onPreselect(e: Event) {
      const { insurerId: nextInsurerId, packageId: nextPackageId } = (
        e as CustomEvent<CoveragePreselectDetail>
      ).detail
      const ins = findInsurer(data, nextInsurerId)
      if (!ins || !ins.packages.some((p) => p.id === nextPackageId)) return
      trackStart()
      if (nextInsurerId !== insurerId) {
        setInsurerId(nextInsurerId)
        setInsurerFreetext("")
        setServiceId(DEFAULT_MEMBERSHIP_ID)
        setMembershipId(DEFAULT_MEMBERSHIP_ID)
        setPhase("select")
      }
      manualNav.current = true
      setPackageId(nextPackageId)
      setPackageFreetext("")
      setPackageHint(false)
      setActiveStep("service")
      trackEvent("insurance_form_package_selected", {
        package_id: nextPackageId,
        package_name:
          ins.packages.find((p) => p.id === nextPackageId)?.name ??
          nextPackageId,
      })
    }
    window.addEventListener(COVERAGE_PRESELECT_EVENT, onPreselect)
    return () =>
      window.removeEventListener(COVERAGE_PRESELECT_EVENT, onPreselect)
  }, [data, insurerId, trackStart])

  function handleAddonsContinue() {
    revealAfterSelections()
  }

  function handleAddonToggle(addon: Addon) {
    const wasSelected = selectedAddons.has(addon.id)
    setSelectedAddons((prev) => {
      const next = new Map(prev)
      if (next.has(addon.id)) next.delete(addon.id)
      else next.set(addon.id, addon)
      return next
    })
    trackEvent(
      wasSelected ? "insurance_addon_removed" : "insurance_addon_added",
      {
        addon_id: addon.id,
        addon_name: addon.name,
        addon_price: addon.price,
        service_id: serviceId,
      }
    )
  }

  /** Load the add-on offer for a service, priced for this visitor. Returns the
   *  list so a caller can decide whether there is an add-ons step to go to. */
  async function loadAddons(
    nextServiceId: string,
    isMember: boolean
  ): Promise<Addon[]> {
    setAddonsLoading(true)
    const addons = await getAddonsForService(nextServiceId, isMember)
    setAddonsLoading(false)
    setAvailableAddons(addons)
    return addons
  }

  /** A check-up was picked: Core / Advanced / Pro MRI or the blood test. Any
   *  membership tier selected above SURVIVES this — it is what prices the
   *  check-up — so this only replaces the check-up and moves on. */
  function handleServiceChange(id: string) {
    trackStart()
    manualNav.current = false
    setPackageHint(false)
    setSelectedAddons(new Map())
    setAvailableAddons([])

    // The check-up was cleared by a second click on it. A membership selected
    // above goes back to being the quoted service; with neither, the step is
    // simply back to nothing chosen. No `service_selected` event either way.
    if (!id) {
      setServiceId(membershipId)
      setPhase("select")
      if (membershipId && insurerReady) {
        loadAddons(membershipId, true).then(() => setSelectedAddons(new Map()))
      }
      return
    }

    setServiceId(id)

    const svc = findService(id)
    trackEvent("insurance_form_service_selected", {
      service_id: id,
      service_name: svc ? getServiceName(svc, locale as Locale) : id,
      // The check-up alone doesn't say what it cost: with a tier selected this
      // is a member's MRI at the member rate, and the funnel should be able
      // to tell the two apart.
      membership_id: membershipId || undefined,
    })

    if (insurerReady) {
      loadAddons(id, !!membershipId).then((addons) => {
        if (addons.length > 0) {
          // Advance to addons step
          setTimeout(() => setActiveStep("addons"), 300)
        } else {
          // No addons — reveal the gate.
          revealAfterSelections()
        }
      })
    }
  }

  /** A membership tier was picked (1x, 2x or 4x). This never advances the step:
   *  the tier re-prices the check-ups below it, and unmounting the step in the
   *  same tick would leave those new prices on screen for no frames at all. */
  function handleMembershipChange(id: string) {
    trackStart()
    manualNav.current = false
    setMembershipId(id)
    setPackageHint(false)
    setPhase("select")

    // With no check-up chosen, the membership IS the thing being quoted (its
    // coverage falls back to the package's blood-test rule). Once a check-up
    // is chosen the membership steps back into being the price context, and
    // `serviceId` stays on the check-up.
    const quoted = !serviceId || isMembershipService(serviceId) ? id : serviceId
    setServiceId(quoted)

    if (id) {
      const svc = findService(id)
      trackEvent("insurance_form_service_selected", {
        service_id: id,
        service_name: svc ? getServiceName(svc, locale as Locale) : id,
        membership_id: id,
      })
    }

    if (!quoted || !insurerReady) {
      setAvailableAddons([])
      setSelectedAddons(new Map())
      return
    }

    // Re-price the add-on offer: same products, different figures, and the
    // running total and the estimate both read them. Keep whatever was
    // ticked, at its new price, rather than silently quoting the old one.
    loadAddons(quoted, !!id).then((addons) => {
      setSelectedAddons((prev) => {
        const next = new Map<string, Addon>()
        for (const addon of addons) {
          if (prev.has(addon.id)) next.set(addon.id, addon)
        }
        return next
      })
    })
  }

  /** The check-up step's own CTA, shown while a membership tier is selected
   *  (the one selection that doesn't move the visitor on). Its next stop is
   *  the add-ons step when the quoted service has add-ons, the estimate
   *  otherwise. */
  function handleMembershipContinue() {
    if (availableAddons.length > 0) {
      setActiveStep("addons")
      return
    }
    revealAfterSelections()
  }

  function getTrackingParams(): Record<string, string> {
    if (typeof window === "undefined") return {}
    const params = new URLSearchParams(window.location.search)
    const result: Record<string, string> = {}
    for (const key of [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_content",
      "utm_term",
      "gclid",
    ]) {
      const val = params.get(key)
      if (val) result[key] = val
    }
    return result
  }

  function buildGatePayload() {
    const svc = findService(serviceId)
    const ins = findInsurer(data, insurerId)

    let iName = ""
    if (isOther) iName = insurerFreetext || t("form.insurer.other")
    else if (ins) iName = ins.name

    let pName = ""
    if (ins && packageId) {
      const pkg = ins.packages.find((p) => p.id === packageId)
      if (pkg)
        pName =
          packageId.endsWith("-other") && packageFreetext
            ? packageFreetext
            : pkg.name
    }

    const addonList = Array.from(selectedAddons.values()).map((a) => ({
      id: a.id,
      key: a.humanReadableId,
      name: a.name,
      price: a.price,
    }))

    return {
      email,
      insurer_id: insurerId,
      insurer_name: iName,
      insurer_freetext: insurerFreetext,
      package_id: packageId,
      package_name: pName,
      package_freetext: packageFreetext,
      service_id: serviceId,
      service_name: svc ? svc.name : "",
      // Take the figures computePanelResult already derived — do NOT re-derive
      // them. This used to compute a base-only reimbursement off svc.price,
      // which silently dropped every selected add-on: the result panel and the
      // pro forma both send the add-on-inclusive totals, so the results email
      // (and its subject line, which interpolates coverageAmount) quoted a
      // smaller number than the one the user had just seen on screen.
      // Same expressions as the proforma payload below.
      service_price: totalPrice,
      coverage_amount: isKnownCoverage
        ? totalReimbursement
        : isLikelyCoverage
          ? (likelyReimbursement ?? null)
          : null,
      addons: addonList.length > 0 ? addonList : undefined,
      locale,
      // Lets the gate route merge this anonymous browser person into the
      // email-hash person server-side (capture-time email→app identity stitch).
      posthog_distinct_id: getPostHogDistinctId(),
      ...getTrackingParams(),
    }
  }

  // Email captured → the (already "calculated") result reveals immediately.
  function handleEmailSubmit() {
    if (!email.trim() || !EMAIL_REGEX.test(email.trim())) {
      setEmailInvalid(true)
      emailRef.current?.focus()
      return
    }
    setEmailInvalid(false)

    fireAndForget("/api/insurance/gate", {
      ...buildGatePayload(),
      result_type: resultType,
    })

    // No `email` here (AHD-9528). The address reaches the backend via the
    // gate POST above, which forwards `posthog_distinct_id` so email-flows can
    // do the capture-time email→app person stitch. PostHog does not need the
    // raw address to attribute the lead.
    trackEvent("insurance_form_submitted", {
      is_internal: isInternalEmail(email),
      insurer_id: insurerId,
      service_id: serviceId,
      locale,
      result_type: resultType,
    })
    // "We captured an email" funnel metric.
    trackEvent("insurance_email_captured", {
      source: "gate",
      insurer_id: insurerId,
      service_id: serviceId,
      result_type: resultType,
    })

    setPhase("result")
  }

  // Back from the "ready" gate screen into the accordion to change something.
  // Lands on the last selection step; every earlier step stays completed and
  // clickable, so anything can be revisited before the email is handed over.
  function handleEditSelection() {
    manualNav.current = true
    setPhase("select")
    setActiveStep(availableAddons.length > 0 ? "addons" : "service")
  }

  function handleReset() {
    if (submitTimer.current) clearTimeout(submitTimer.current)
    setInsurerId("")
    setPackageId("")
    setServiceId(DEFAULT_MEMBERSHIP_ID)
    setMembershipId(DEFAULT_MEMBERSHIP_ID)
    setInsurerFreetext("")
    setPackageFreetext("")
    setPackageHint(false)
    setEmail("")
    setSubmitError(false)
    setPhase("select")
    setActiveStep("insurer")
    setSelectedAddons(new Map())
    setAvailableAddons([])
    setAddonsLoading(false)
    setShowProformaForm(false)
    setProformaFirstName("")
    setProformaLastName("")
    setProformaGender("")
    setProformaStatus("idle")
    setProformaPdfUrl(null)
    setProformaError(null)
    manualNav.current = false
    hasStarted.current = false
  }

  async function handleProformaRequest() {
    if (!proformaFirstName.trim() || !proformaLastName.trim()) return

    // The email is captured at the gate before the result renders.
    const recipient = email.trim()
    if (!recipient || !EMAIL_REGEX.test(recipient)) return

    setProformaStatus("submitting")
    setProformaError(null)

    trackEvent("proforma_form_submitted", {
      insurer_id: insurerId,
      result_type: resultType,
    })

    try {
      const addonList = Array.from(selectedAddons.values()).map((a) => ({
        id: a.id,
        key: a.humanReadableId,
        name: a.name,
        price: a.price,
      }))

      const res = await fetch("/api/insurance/proforma-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: recipient,
          firstname: proformaFirstName.trim(),
          lastname: proformaLastName.trim(),
          gender: proformaGender || undefined,
          insurer_id: insurerId,
          insurer_freetext: isOther
            ? insurerFreetext.trim() || undefined
            : undefined,
          package_id: packageId || undefined,
          service_id: serviceId,
          // Prices the PDF from the catalogue's member column, so the document
          // the customer hands their insurer says the figure they were quoted.
          membership_id: membershipId || undefined,
          result_type: resultType,
          coverage_amount: isKnownCoverage
            ? totalReimbursement
            : isLikelyCoverage
              ? (likelyReimbursement ?? undefined)
              : undefined,
          service_price: totalPrice,
          addons: addonList.length > 0 ? addonList : undefined,
          locale,
          // Capture-time email→app identity stitch (no-gate proforma is often
          // the first email we capture for the lead).
          posthog_distinct_id: getPostHogDistinctId(),
          ...getTrackingParams(),
        }),
      })

      if (
        res.status === 200 &&
        res.headers.get("content-type")?.includes("application/pdf")
      ) {
        const blob = await res.blob()
        const url = URL.createObjectURL(blob)
        setProformaPdfUrl(url)
        setProformaStatus("success")
        trackEvent("proforma_requested", {
          mode: "auto",
          result_type: resultType,
          insurer_id: insurerId,
          service_id: serviceId,
        })
      } else if (res.status === 200 || res.status === 201) {
        setProformaStatus("success")
        trackEvent("proforma_requested", {
          mode: "queued",
          result_type: resultType,
          insurer_id: insurerId,
          service_id: serviceId,
        })
      } else if (res.status === 202) {
        setProformaStatus("async_fallback")
        trackEvent("proforma_requested", {
          mode: "async_fallback",
          result_type: resultType,
          insurer_id: insurerId,
          service_id: serviceId,
        })
      } else if (res.status === 429) {
        setProformaError(
          t("proforma.error.rate_limit") ||
            "Too many requests. Please try again later."
        )
        setProformaStatus("error")
        trackEvent("proforma_request_rate_limited", { insurer_id: insurerId })
      } else {
        const body = await res.json().catch(() => ({}))
        setProformaError(
          body.error ||
            t("proforma.error.generic") ||
            "Something went wrong. Please try again."
        )
        setProformaStatus("error")
        trackEvent("proforma_request_failed", {
          status: String(res.status),
          insurer_id: insurerId,
        })
      }
    } catch (err) {
      setProformaError(
        t("proforma.error.network") ||
          "Connection error. Please check your internet and try again."
      )
      setProformaStatus("error")
      // Network-level fetch failures are per-user and unactionable; the user
      // already sees the inline error and the analytics event still fires.
      if (!isFetchNetworkError(err)) {
        captureException(err instanceof Error ? err : new Error(String(err)), {
          source: "proforma-request",
          insurerId,
        })
      }
      trackEvent("proforma_request_failed", {
        status: "0",
        error_type: "network",
        insurer_id: insurerId,
      })
    }
  }

  // Coverage calculation for result screen — pure orchestration lives in
  // lib/insurance/panel-result.ts (no React/state dependency) so this same
  // function backs both the panel (called here) and the cross-insurer
  // comparison invariant test matrix, which imports it directly instead of
  // re-deriving the formula by hand. #2247 was the panel and the comparison
  // row drifting apart because they were computed independently in two
  // files; this keeps there being only one computation to drift from.
  const panelAddons = Array.from(selectedAddons.values())
  // What the visitor pays for the quoted service. A member's Core MRI is
  // invoiced at the member rate, and an insurer reimburses a percentage of
  // the invoice, so this figure has to go INTO the calc rather than being
  // applied to its result.
  const quotedService = serviceId ? findService(serviceId) : undefined
  const quotedPrice = quotedService
    ? effectiveServicePrice(quotedService, !!membershipId)
    : undefined
  const {
    service,
    coverage,
    isItemized,
    isKnownCoverage,
    isLikelyCoverage,
    isPackageNotCovered,
    reimbursement,
    addonTotalPrice,
    totalReimbursement,
    likelyReimbursement,
    totalPrice,
    outOfPocket,
    likelyOutOfPocket,
    resultType,
    ranked,
    membershipPrice,
  } = computePanelResult(
    data,
    insurerId,
    packageId,
    serviceId,
    panelAddons,
    quotedPrice,
    // Only when it is a SECOND product. A membership picked on its own is
    // already the quoted service, and passing it here too would bill it twice.
    membershipId && membershipId !== serviceId
      ? {
          serviceId: membershipId,
          price: findService(membershipId)?.price ?? 0,
        }
      : undefined
  )

  const showProformaButton = shouldShowProforma(data, insurerId)

  useEffect(() => {
    // The email gate became visible. Without this event the gate is invisible
    // in analytics: `insurance_form_submitted` fires when the address is handed
    // over, so everyone who saw the gate and left is indistinguishable from
    // everyone who abandoned an earlier accordion step. That gap hides the
    // funnel's largest single drop — ~4.3k of the 6.1k who pick a service never
    // reach `insurance_form_submitted`, and today we cannot attribute any of it.
    //
    // `result_type` is the reason this carries the full selection: the coverage
    // outcome is already known here (the "calculating" phase computed it, the
    // gate just withholds it), so pairing it with the submit event is what makes
    // "does the not-covered segment abandon the gate differently?" answerable.
    //
    // Fires per gate view, not per visitor: "Edit selection" returns to the
    // accordion and completing it again re-enters this phase. That matches
    // `insurance_result_viewed` below — count uniques on `person_id` when you
    // want people rather than views.
    if (phase === "gate-email") {
      trackEvent("insurance_email_gate_shown", {
        result_type: resultType,
        insurer_id: insurerId,
        package_id: packageId,
        service_id: serviceId,
        locale,
      })
    }
    if (phase === "result") {
      trackEvent("insurance_result_viewed", {
        result_type: resultType,
        insurer_id: insurerId,
        package_id: packageId,
        service_id: serviceId,
        // Itemized reports its amount too — it's a concrete figure, just shown
        // as an estimate rather than a confirmed one.
        coverage_amount:
          isKnownCoverage && service
            ? String(reimbursement)
            : isItemized && likelyReimbursement !== null
              ? String(likelyReimbursement)
              : null,
      })
      if (showProformaButton) {
        trackEvent("proforma_button_shown", {
          insurer_id: insurerId,
          result_type: resultType,
        })
      }
      if (ranked.length > 0) {
        trackEvent("insurance_comparison_viewed", {
          insurer_id: insurerId,
          service_id: serviceId,
          result_type: resultType,
          insurer_count: ranked.length,
        })
      }
    }
  }, [phase]) // eslint-disable-line react-hooks/exhaustive-deps

  // Bring the loader / result card into view once it mounts. By the time a
  // user reaches the result (after the gate loader) they've usually scrolled,
  // so anchor the outcome to the top of the viewport instead of leaving it
  // below the fold.
  //
  // Deferred one frame (rAF): firing a smooth scrollIntoView synchronously in
  // the same commit as the phase swap races the card's height change and
  // registers as layout shift (CLS). Waiting a frame lets layout settle first.
  // prefers-reduced-motion users get an instant jump, no smooth animation.
  useEffect(() => {
    if (phase === "select") return
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
    const raf = requestAnimationFrame(() => {
      resultRef.current?.scrollIntoView({
        behavior: prefersReduced ? "auto" : "smooth",
        block: "start",
      })
    })
    return () => cancelAnimationFrame(raf)
  }, [phase])

  // Clear the pending loader→result timer if the form unmounts mid-loader.
  useEffect(
    () => () => {
      if (submitTimer.current) clearTimeout(submitTimer.current)
    },
    []
  )

  const insurerDisplayName = isOther
    ? insurerFreetext || t("form.insurer.other")
    : insurer?.name || ""
  // The `-other` rows carry the literal CMS name "Other" in all three locales,
  // and the pills already override it with the translated label. Without the
  // same override here, a visitor who picks "Andere" / "Autre" and skips the
  // freetext reads "Other" back in the step summary and the result panel.
  const packageDisplayName = packageId.endsWith("-other")
    ? packageFreetext || t("form.package.other_name")
    : insurer?.packages.find((p) => p.id === packageId)?.name || ""

  /** The checker's own clean service name plus the price this visitor pays.
   *  `getServiceName` cannot be used for anything a member reads: its `name_*`
   *  strings have the LIST price baked into them, so the collapsed step named
   *  the list price one line above an estimate built on the member rate. */
  function serviceLabel(id: string): string {
    const svc = findService(id)
    if (!svc) return ""
    const price = effectiveServicePrice(svc, !!membershipId)
    return `${t(`form.service.name.${id}`)} (CHF ${formatCHF(price)})`
  }

  // ── Summaries for collapsed steps ──
  const insurerLogo = insurer ? getInsurerLogoUrl(insurer) : undefined
  const insurerSummaryText = insurer
    ? insurer.name
    : isOther
      ? insurerFreetext || t("form.insurer.other")
      : ""

  const serviceObj = findService(serviceId)
  const serviceIcon = serviceId ? getServiceIcon(serviceId) : undefined
  // Both selections are named when both exist, so the collapsed step still
  // says why the figures below are the member ones.
  const membershipObj =
    membershipId && membershipId !== serviceId
      ? findService(membershipId)
      : undefined
  const serviceSummaryText = serviceObj
    ? [serviceId, membershipObj ? membershipId : undefined]
        .filter((id): id is string => !!id)
        .map(serviceLabel)
        .join(" + ")
    : ""

  const insurerSummary = insurerSummaryText ? (
    <span className="accordion-summary-inline">
      {insurerLogo && (
        <img
          src={insurerLogo}
          alt=""
          width={18}
          height={18}
          className="accordion-summary-logo"
        />
      )}
      <span className="accordion-summary-text">{insurerSummaryText}</span>
    </span>
  ) : null

  const serviceSummary = serviceSummaryText ? (
    <span className="accordion-summary-inline">
      {serviceIcon && (
        <span
          className={cn(
            "accordion-summary-icon",
            serviceIcon.photo && "icon-photo"
          )}
          style={{ background: serviceIcon.bg }}
        >
          <img src={serviceIcon.icon} alt="" width={14} height={14} />
        </span>
      )}
      <span className="accordion-summary-text">{serviceSummaryText}</span>
    </span>
  ) : null

  const addonCount = selectedAddons.size
  const addonTotal = Array.from(selectedAddons.values()).reduce(
    (sum, a) => sum + a.price,
    0
  )
  const addonsSummary =
    addonCount > 0 ? (
      <span className="accordion-summary-text">
        {t("gate.addons.total", {
          count: String(addonCount),
          amount: formatCHF(addonTotal),
        })}
      </span>
    ) : (
      <span className="accordion-summary-text">{t("gate.addons.skip")}</span>
    )

  // Whether the addons step should be visible
  const showAddonsStep = !!serviceId && availableAddons.length > 0
  // Addons step is "completed" once user has clicked continue (advanced past it)
  const addonsCompleted = showAddonsStep && phase !== "select"

  // Dynamic step numbering
  const serviceStepNum = showPlanStep ? 3 : 2
  const addonsStepNum = showAddonsStep ? serviceStepNum + 1 : serviceStepNum

  // ── Calculating loader ──
  // Shown after the last selection while the result is "calculated" (a brief,
  // deliberate pause) — BEFORE the email is asked for. Same card shell +
  // minHeight as the gate and result so the reveals don't jump.
  if (phase === "calculating") {
    return (
      <section className={embedded ? "py-4" : "py-16"}>
        <div
          ref={resultRef}
          className={cn(
            cardClass,
            "flex flex-col items-center justify-center text-center"
          )}
          style={{ minHeight: 480 }}
        >
          <div className="calc-bar" aria-hidden="true">
            <span className="calc-bar-fill" />
          </div>
          <p className="type-title-md mt-6 text-neutral-900">
            {t("gate.checking")}
          </p>
        </div>
      </section>
    )
  }

  // ── Email gate ("your estimate is ready") ──
  // The calculation is done; the email unlocks it. Shows what was checked as
  // chips, so the user sees the estimate is theirs before handing over the
  // address. "Edit selection" drops back into the accordion.
  if (phase === "gate-email") {
    return (
      <section className={embedded ? "py-4" : "py-16"}>
        <div
          ref={resultRef}
          className={cn(cardClass, "gate-ready")}
          style={{ minHeight: 480 }}
        >
          <div className="gate-ready-head">
            <div className="gate-ready-icon" aria-hidden="true">
              <Receipt size={28} weight="regular" />
            </div>
            <h3 className="gate-ready-title">{t("gate.ready.title")}</h3>
            <p className="gate-ready-desc">{t("gate.ready.desc")}</p>
          </div>

          {/* Centred 460px column: what was checked (left-aligned), the
              email field, the button and the free line share one left edge;
              the head and the legal lines stay full-width centred. */}
          <div className="gate-ready-col">
            <div className="gate-ready-selection">
              <span className="gate-ready-selection-primary">
                {insurerLogo && (
                  <img
                    src={insurerLogo}
                    alt=""
                    width={32}
                    height={32}
                    className="gate-ready-selection-logo"
                  />
                )}
                {[insurerSummaryText, packageDisplayName]
                  .filter(Boolean)
                  .join(" ")}
              </span>
              {/* Check-up, add-ons and the way back share the second line. */}
              <span className="gate-ready-selection-secondary">
                {[
                  // The membership leads: it is the thing that prices
                  // everything after it. One segment, label + tier, because
                  // separating them with a middle dot reads as two products
                  // once a check-up follows on the same line.
                  membershipId
                    ? `${t("form.service.membership.label")} ${t(
                        `form.service.name.${membershipId}`
                      )}`
                    : null,
                  // The check-up, when one is chosen. Skipped when `serviceId`
                  // IS the tier (a membership on its own), which the segment
                  // above already named.
                  serviceId && !isMembershipService(serviceId)
                    ? t(`form.service.name.${serviceId}`)
                    : null,
                  addonCount > 0
                    ? t(
                        // The form's translator interpolates but doesn't
                        // pluralize, and "1 add-on(s)" sat on the last screen
                        // before the email field.
                        addonCount === 1
                          ? "gate.addons.count_one"
                          : "gate.addons.count_other",
                        { count: String(addonCount) }
                      )
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
                {"\u00a0·\u00a0"}
                <button
                  type="button"
                  className="gate-ready-edit"
                  onClick={handleEditSelection}
                >
                  {t("gate.ready.edit")}
                </button>
              </span>
            </div>

            <div className="gate-ready-form">
              <input
                ref={emailRef}
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  if (emailInvalid) setEmailInvalid(false)
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    handleEmailSubmit()
                  }
                }}
                placeholder={t("form.email.placeholder")}
                aria-label={t("form.email.label")}
                aria-invalid={emailInvalid || undefined}
                className={cn(
                  "gate-input",
                  emailInvalid && "gate-input-invalid"
                )}
              />
              {emailInvalid && (
                <p className="gate-input-error" role="alert">
                  {t("form.validation.email")}
                </p>
              )}
              <button
                type="button"
                className="btn-primary"
                onClick={handleEmailSubmit}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                {t("gate.submit")}
              </button>
              <p className="gate-ready-free">{t("gate.ready.free")}</p>
            </div>
          </div>

          {submitError && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-center text-sm text-red-700"
            >
              {t("form.error.message")}
            </motion.div>
          )}

          <p className="privacy-notice">
            {t("gate.privacy")}{" "}
            <a
              href={`/ch/${locale}/privacy`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("gate.privacy.link")}
            </a>
            {t("gate.privacy.suffix")}
          </p>
          <p className="privacy-notice">
            {t("gate.terms")}{" "}
            <a
              href={`/ch/${locale}/insurance-checker-terms`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("gate.terms.link")}
            </a>
            {t("gate.terms.suffix")}
          </p>
        </div>
      </section>
    )
  }

  // ── Result screen ──
  // Extracted to coverage-result.tsx (AHD-7879) to keep this render tree small;
  // it renders plain DOM via the same static-motion shim (no framer runtime).
  if (phase === "result") {
    return (
      <CoverageResult
        embedded={embedded}
        servicePrice={quotedPrice ?? 0}
        membershipPrice={membershipPrice}
        membershipName={
          membershipId && membershipId !== serviceId
            ? t(`form.service.name.${membershipId}`)
            : undefined
        }
        cardClass={cardClass}
        resultRef={resultRef}
        resultType={resultType}
        service={service}
        ranked={ranked}
        coveragePeriod={coverage?.period}
        totalReimbursement={totalReimbursement}
        addonTotalPrice={addonTotalPrice}
        outOfPocket={outOfPocket}
        likelyReimbursement={likelyReimbursement}
        likelyOutOfPocket={likelyOutOfPocket}
        insurerDisplayName={insurerDisplayName}
        packageDisplayName={packageDisplayName}
        isPackageNotCovered={isPackageNotCovered}
        coverageInfoUrl={insurer?.coverageInfoUrl ?? null}
        selectedAddonProductIds={Array.from(selectedAddons.keys())}
        insurerId={insurerId}
        serviceId={serviceId}
        email={email}
        showProformaButton={showProformaButton}
        showProformaForm={showProformaForm}
        setShowProformaForm={setShowProformaForm}
        proformaFirstName={proformaFirstName}
        setProformaFirstName={setProformaFirstName}
        proformaLastName={proformaLastName}
        setProformaLastName={setProformaLastName}
        proformaGender={proformaGender}
        setProformaGender={setProformaGender}
        proformaStatus={proformaStatus}
        proformaPdfUrl={proformaPdfUrl}
        proformaError={proformaError}
        setProformaError={setProformaError}
        setProformaStatus={setProformaStatus}
        handleProformaRequest={handleProformaRequest}
        handleReset={handleReset}
      />
    )
  }

  // ── Form screen (accordion) ──
  return (
    <section className={embedded ? "py-4" : "py-16"}>
      <div className={cardClass} style={{ minHeight: 480 }}>
        {/* Step 1: Insurer */}
        <AccordionStep
          stepNumber={1}
          label={t("gate.section.insurer")}
          isActive={activeStep === "insurer"}
          isCompleted={!!insurerId}
          onActivate={() => {
            manualNav.current = true
            setActiveStep("insurer")
          }}
          summary={insurerSummary}
        >
          <InsurerDropdown
            insurers={data.insurers}
            insurerId={insurerId}
            onInsurerChange={handleInsurerChange}
            onFreetext={setInsurerFreetext}
            insurerFreetext={insurerFreetext}
            onAdvance={() => setActiveStep("service")}
          />
        </AccordionStep>

        {/* Step 2: Plan (always visible, hidden only for "Other") */}
        {showPlanStep && (
          <AccordionStep
            stepNumber={2}
            label={t("gate.section.plan")}
            isActive={activeStep === "plan"}
            isCompleted={hasPackage}
            isEnabled={needsPackage}
            onActivate={() => {
              manualNav.current = true
              setActiveStep("plan")
            }}
            summary={
              packageDisplayName ? (
                <span className="accordion-summary-text">
                  {packageDisplayName}
                </span>
              ) : null
            }
          >
            {needsPackage ? (
              <>
                {/* INS-85: people were reading the estimate as a promise of
                    reimbursement, then finding out their actual package was a
                    different one. Ask them to check the policy before picking. */}
                <p className="package-info">
                  <svg
                    className="package-info-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  <span>{t("form.package.info")}</span>
                </p>
                <div className="package-pills">
                  {insurer?.packages.map((pkg) => (
                    <button
                      key={pkg.id}
                      type="button"
                      className={cn(
                        "package-pill",
                        pkg.id === packageId && "selected"
                      )}
                      onClick={() => handlePackageChange(pkg.id)}
                    >
                      {pkg.id.endsWith("-other")
                        ? t("form.package.other_name")
                        : pkg.name}
                    </button>
                  ))}
                </div>
                {/* Other package freetext */}
                {packageId.endsWith("-other") && (
                  <motion.div
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.3, ease: [0.25, 1, 0.5, 1] }}
                    style={{ marginTop: 12 }}
                  >
                    <input
                      type="text"
                      value={packageFreetext}
                      onChange={(e) => setPackageFreetext(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault()
                          setActiveStep("service")
                        }
                      }}
                      placeholder={t("form.package.freetext_placeholder")}
                      aria-label={t("form.package.freetext_label")}
                      className="gate-input"
                      autoFocus
                    />
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => setActiveStep("service")}
                      style={{ marginTop: 12 }}
                    >
                      {t("gate.continue")}
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </button>
                  </motion.div>
                )}
              </>
            ) : null}
          </AccordionStep>
        )}

        {/* Step 3: Service */}
        <AccordionStep
          stepNumber={serviceStepNum}
          label={t("gate.section.service")}
          isActive={activeStep === "service"}
          isCompleted={!!serviceId}
          isEnabled={insurerReady}
          onActivate={() => {
            manualNav.current = true
            setActiveStep("service")
            setPhase("select")
          }}
          summary={serviceSummary}
        >
          <ServiceDropdown
            serviceId={isMembershipService(serviceId) ? "" : serviceId}
            membershipId={membershipId}
            onServiceChange={handleServiceChange}
            onMembershipChange={handleMembershipChange}
          />
          {/* Membership tiers don't auto-advance (see handleServiceChange), so
              they need their own way on. Same sticky treatment as the add-ons
              step; the label follows where it actually goes. */}
          {!!membershipId && (
            <div className="addon-cta">
              <button
                type="button"
                className="btn-primary"
                onClick={handleMembershipContinue}
              >
                {availableAddons.length > 0
                  ? t("gate.addons.continue")
                  : t("gate.addons.estimate")}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </button>
            </div>
          )}
        </AccordionStep>

        {/* Step: Add-ons (only if addons available for the selected service) */}
        {showAddonsStep && (
          <AccordionStep
            stepNumber={addonsStepNum}
            label={t("gate.section.addons")}
            isActive={activeStep === "addons"}
            isCompleted={addonsCompleted}
            isEnabled={!!serviceId}
            onActivate={() => {
              manualNav.current = true
              setActiveStep("addons")
            }}
            summary={addonsSummary}
            aside={
              addonCount > 0 ? (
                <span className="addon-total">
                  {t("gate.addons.total", {
                    count: String(addonCount),
                    amount: formatCHF(addonTotal),
                  })}
                </span>
              ) : null
            }
          >
            <AddonSelector
              addons={availableAddons}
              selectedIds={new Set(selectedAddons.keys())}
              onToggle={handleAddonToggle}
            />
            {/* On phones the CTA sticks to the bottom of the viewport while
                the add-on list is on screen (see .addon-cta). */}
            <div className="addon-cta">
              <button
                type="button"
                className="btn-primary"
                onClick={handleAddonsContinue}
              >
                {selectedAddons.size > 0
                  ? t("gate.addons.estimate")
                  : t("gate.addons.skip_estimate")}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </button>
            </div>
          </AccordionStep>
        )}
      </div>
    </section>
  )
}
